import { useState, useEffect, useMemo } from "react";
import { createAdminApiClient, resolveImageUrl } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Plus, Pencil, Trash2, Eye, EyeOff, Upload } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const AdminBlog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [form, setForm] = useState({
        title: "", slug: "", content: "", excerpt: "", featured_image: "",
        author: "Dry Ice Bulls", tags: [], is_published: false, seo_title: "", seo_description: ""
    });
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const getAdminApi = () => {
        const creds = JSON.parse(sessionStorage.getItem("adminCredentials") || "{}");
        return createAdminApiClient(creds.username, creds.password);
    };

    const fetchPosts = async () => {
        try {
            const api = getAdminApi();
            const res = await api.getBlogPosts();
            setPosts(res.data);
        } catch (err) {
            console.error("Failed to load blog posts:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPosts(); }, []);

    const openNew = () => {
        setEditingPost(null);
        setForm({
            title: "", slug: "", content: "", excerpt: "", featured_image: "",
            author: "Dry Ice Bulls", tags: [], is_published: false, seo_title: "", seo_description: ""
        });
        setShowEditor(true);
    };

    const openEdit = (post) => {
        setEditingPost(post);
        setForm({
            title: post.title || "",
            slug: post.slug || "",
            content: post.content || "",
            excerpt: post.excerpt || "",
            featured_image: post.featured_image || "",
            author: post.author || "Dry Ice Bulls",
            tags: post.tags || [],
            is_published: post.is_published || false,
            seo_title: post.seo_title || "",
            seo_description: post.seo_description || "",
        });
        setShowEditor(true);
    };

    const generateSlug = (title) => {
        return title.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-");
    };

    const handleTitleChange = (val) => {
        setForm(prev => ({
            ...prev,
            title: val,
            slug: !editingPost ? generateSlug(val) : prev.slug
        }));
    };

    const addTag = () => {
        const tag = tagInput.trim();
        if (tag && !form.tags.includes(tag)) {
            setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        }
        setTagInput("");
    };

    const removeTag = (tag) => {
        setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const api = getAdminApi();
            const formData = new FormData();
            formData.append("file", file);
            const creds = JSON.parse(sessionStorage.getItem("adminCredentials") || "{}");
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-image`, {
                method: "POST",
                headers: { Authorization: "Basic " + btoa(`${creds.username}:${creds.password}`) },
                body: formData
            });
            const data = await res.json();
            if (data.url) {
                const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                setForm(prev => ({ ...prev, featured_image: fullUrl }));
            }
        } catch (err) {
            console.error("Upload failed:", err);
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            const api = getAdminApi();
            if (editingPost) {
                await api.updateBlogPost(editingPost.id, form);
            } else {
                await api.createBlogPost(form);
            }
            setShowEditor(false);
            fetchPosts();
        } catch (err) {
            console.error("Save failed:", err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (postId) => {
        if (!window.confirm("Delete this blog post?")) return;
        try {
            const api = getAdminApi();
            await api.deleteBlogPost(postId);
            fetchPosts();
        } catch (err) {
            console.error("Delete failed:", err);
        }
    };

    const quillModules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote", "link", "image"],
            [{ align: [] }],
            ["clean"]
        ]
    }), []);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    return (
        <div data-testid="admin-blog">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Blog Posts</h1>
                    <p className="text-muted-foreground">Create and manage blog content for SEO</p>
                </div>
                <Button className="rounded-none gap-2" onClick={openNew} data-testid="admin-blog-new-btn">
                    <Plus className="h-4 w-4" /> New Post
                </Button>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1,2,3].map(i => <div key={i} className="h-20 bg-card border border-border animate-pulse" />)}
                </div>
            ) : posts.length === 0 ? (
                <Card className="rounded-none border-border">
                    <CardContent className="p-12 text-center">
                        <p className="text-muted-foreground mb-4">No blog posts yet</p>
                        <Button className="rounded-none" onClick={openNew}>Create Your First Post</Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {posts.map(post => (
                        <Card key={post.id} className="rounded-none border-border" data-testid={`admin-blog-card-${post.id}`}>
                            <CardContent className="p-4 flex items-center gap-4">
                                {post.featured_image && (
                                    <img src={resolveImageUrl(post.featured_image)} alt="" className="w-16 h-16 object-cover border border-border flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-heading font-bold truncate">{post.title}</p>
                                        {post.is_published ? (
                                            <Badge className="rounded-none bg-green-500/20 text-green-500 text-xs"><Eye className="h-3 w-3 mr-1" />Live</Badge>
                                        ) : (
                                            <Badge variant="outline" className="rounded-none text-xs"><EyeOff className="h-3 w-3 mr-1" />Draft</Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{formatDate(post.published_at || post.created_at)} · /{post.slug}</p>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    <Button variant="outline" size="icon" className="rounded-none" onClick={() => openEdit(post)} data-testid={`admin-blog-edit-${post.id}`}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" size="icon" className="rounded-none text-destructive hover:text-destructive" onClick={() => handleDelete(post.id)} data-testid={`admin-blog-delete-${post.id}`}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Editor Dialog */}
            <Dialog open={showEditor} onOpenChange={setShowEditor}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-black text-xl">
                            {editingPost ? "Edit Post" : "New Blog Post"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Title</label>
                            <Input
                                value={form.title}
                                onChange={e => handleTitleChange(e.target.value)}
                                className="rounded-none"
                                placeholder="Post title"
                                data-testid="blog-editor-title"
                            />
                        </div>

                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Slug</label>
                            <Input
                                value={form.slug}
                                onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                                className="rounded-none"
                                placeholder="url-friendly-slug"
                                data-testid="blog-editor-slug"
                            />
                        </div>

                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Excerpt</label>
                            <Input
                                value={form.excerpt}
                                onChange={e => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
                                className="rounded-none"
                                placeholder="Brief summary for listing pages"
                                data-testid="blog-editor-excerpt"
                            />
                        </div>

                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Featured Image</label>
                            <div className="flex gap-2">
                                <Input
                                    value={form.featured_image}
                                    onChange={e => setForm(prev => ({ ...prev, featured_image: e.target.value }))}
                                    className="rounded-none flex-1"
                                    placeholder="Image URL"
                                />
                                <label className="cursor-pointer">
                                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                                    <Button variant="outline" className="rounded-none gap-2" asChild disabled={uploading}>
                                        <span><Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}</span>
                                    </Button>
                                </label>
                            </div>
                            {form.featured_image && (
                                <img src={resolveImageUrl(form.featured_image)} alt="Preview" className="mt-2 h-32 object-cover border border-border" />
                            )}
                        </div>

                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Content</label>
                            <div className="border border-border bg-background">
                                <ReactQuill
                                    theme="snow"
                                    value={form.content}
                                    onChange={val => setForm(prev => ({ ...prev, content: val }))}
                                    modules={quillModules}
                                    className="[&_.ql-toolbar]:border-border [&_.ql-container]:border-border [&_.ql-editor]:min-h-[300px]"
                                    data-testid="blog-editor-content"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Tags</label>
                            <div className="flex gap-2 mb-2">
                                <Input
                                    value={tagInput}
                                    onChange={e => setTagInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                                    className="rounded-none"
                                    placeholder="Add a tag and press Enter"
                                    data-testid="blog-editor-tag-input"
                                />
                                <Button variant="outline" className="rounded-none" onClick={addTag}>Add</Button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {form.tags.map(tag => (
                                    <Badge key={tag} variant="outline" className="rounded-none gap-1">
                                        {tag}
                                        <button onClick={() => removeTag(tag)} className="ml-1 hover:text-destructive">&times;</button>
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Author</label>
                                <Input
                                    value={form.author}
                                    onChange={e => setForm(prev => ({ ...prev, author: e.target.value }))}
                                    className="rounded-none"
                                    data-testid="blog-editor-author"
                                />
                            </div>
                            <div className="flex items-end gap-3">
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Published</label>
                                    <div className="flex items-center gap-2 h-10">
                                        <Switch
                                            checked={form.is_published}
                                            onCheckedChange={val => setForm(prev => ({ ...prev, is_published: val }))}
                                            data-testid="blog-editor-published-switch"
                                        />
                                        <span className="text-sm">{form.is_published ? "Live" : "Draft"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <details className="border border-border p-4">
                            <summary className="font-mono text-xs uppercase tracking-wider text-muted-foreground cursor-pointer">SEO Settings</summary>
                            <div className="space-y-3 mt-3">
                                <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">SEO Title</label>
                                    <Input
                                        value={form.seo_title}
                                        onChange={e => setForm(prev => ({ ...prev, seo_title: e.target.value }))}
                                        className="rounded-none"
                                        placeholder="Custom page title for search engines"
                                        data-testid="blog-editor-seo-title"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">SEO Description</label>
                                    <Input
                                        value={form.seo_description}
                                        onChange={e => setForm(prev => ({ ...prev, seo_description: e.target.value }))}
                                        className="rounded-none"
                                        placeholder="Meta description for search engines"
                                        data-testid="blog-editor-seo-description"
                                    />
                                </div>
                            </div>
                        </details>

                        <div className="flex gap-3 pt-4">
                            <Button
                                onClick={handleSave}
                                disabled={saving || !form.title.trim()}
                                className="rounded-none"
                                data-testid="blog-editor-save-btn"
                            >
                                {saving ? "Saving..." : editingPost ? "Update Post" : "Create Post"}
                            </Button>
                            <Button variant="outline" className="rounded-none" onClick={() => setShowEditor(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminBlog;
